import React, { useEffect, useRef, useState } from 'react';
import { Connect } from 'connect-web-sdk';
import { useArgyleIncomeWorkflow } from '../../../hooks/useArgyleIncomeWorkflow';
import { httpClient } from '../../../dataproviders/baseDataProvider';
import { ATS_API_URL } from '../../../config';
import { ARGYLE_BANK_PENDING_HINT_PREFIX } from '../../../utils/argyleConfig';

type Props = {
  trn_id?: string | null;
  type: 'bank' | 'payroll';
  label?: React.ReactNode;
  subtitle?: React.ReactNode;
  asButton?: boolean;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  onLinkClicked?: () => void;
  onArgyleSuccess?: () => void;
  onArgyleExit?: (completed: boolean) => void;
  onDone?: () => void;
  linkTokenData?: Record<string, any>;
  onMissingBorrowerFields?: (fields: string[]) => void;
  onBankRemoved?: () => void;
};

const defaults = {
  bank: {
    label: 'Verify Income through bank deposits',
    subtitle:
      '(This may require you to connect to your bank.)',
  },
  payroll: {
    label: 'Verify Income through payroll providers',
    subtitle:
      '(You may be redirected to connect to your payroll provider.)',
  },
} as const;

const ArgyleIncomeLink: React.FC<Props> = ({
  trn_id,
  type,
  label,
  subtitle,
  asButton = false,
  className,
  style,
  disabled = false,
  onLinkClicked,
  onArgyleSuccess,
  onArgyleExit,
  onDone,
  linkTokenData,
  onMissingBorrowerFields,
  onBankRemoved,
}) => {
  const [bankConnectUrl, setBankConnectUrl] = useState<string | null>(null);
  const [bankConnectError, setBankConnectError] = useState<string | null>(null);
  const bankEmbedNodeRef = useRef<HTMLDivElement | null>(null);
  const launchedUrlRef = useRef<string | null>(null);
  const closingRef = useRef<boolean>(false);
  const { start, cancel, reportIncompleteExit, disabled: workflowDisabled } = useArgyleIncomeWorkflow({
    type,
    trn_id,
    onDone,
    onArgyleSuccess,
    onBeforeOpen: onLinkClicked,
    onExit: onArgyleExit,
    linkTokenData,
    onBankConnectUrl: type === 'bank' ? (url) => setBankConnectUrl(url) : undefined,
    onMissingBorrowerFields,

  });

  const closeBankModal = (completed: boolean) => {
    closingRef.current = true;
    try {
      Connect.destroy();
    } catch {
      // no-op: ensure UI still closes even if SDK destroy throws
    }
    launchedUrlRef.current = null;
    setBankConnectUrl(null);
    setBankConnectError(null);
    if (!completed) {
      reportIncompleteExit();
      return;
    }
    onArgyleExit?.(true);
    if (completed) {
      onArgyleSuccess?.();
      onDone?.();
    }
  };

  useEffect(() => {
    if (type !== 'bank' || !bankConnectUrl || !bankEmbedNodeRef.current) return;
    if (launchedUrlRef.current === bankConnectUrl) return;

    closingRef.current = false;
    setBankConnectError(null);
    launchedUrlRef.current = bankConnectUrl;

    try {
      Connect.launch(
        bankConnectUrl,
        {
          onDone: () => {
            if (trn_id && typeof window !== 'undefined') {
              sessionStorage.setItem(`${ARGYLE_BANK_PENDING_HINT_PREFIX}${trn_id}`, String(Date.now()));
            }
            closeBankModal(true);
          },
          onCancel: () => closeBankModal(false),
          onError: () => closeBankModal(false),
          onUser: async (event: any) => {
            if (event?.action === 'RemoveConnectedInstitutionSuccess') {
              try {
                await httpClient(`${ATS_API_URL}/argyle/bankInstitutionRemoved`, {
                  method: 'POST',
                  body: JSON.stringify({
                    trn_id,
                    institution_id: event.institutionId,
                    institution_login_id: event.institutionLoginId,
                  }),
                });
              } catch {
                // no-op: removal is best-effort, UI already updated via onBankRemoved
              }
              onBankRemoved?.();
            }
          },
        },
        {
          node: bankEmbedNodeRef.current,
        }
      );
    } catch (e: any) {
      launchedUrlRef.current = null;
      setBankConnectError(e?.message || 'Unable to launch bank connect');
      reportIncompleteExit();
    }
  }, [type, bankConnectUrl, reportIncompleteExit]);

  useEffect(() => {
    return () => {
      try {
        Connect.destroy();
      } catch {
        // no-op
      }
    };
  }, []);

  if (!trn_id) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled || workflowDisabled) return;
    start();
  };

  const content = (
    <>
      {label ?? defaults[type].label}
      {subtitle !== '' && (
        <div style={{ fontSize: '0.8125rem', color: '#444', lineHeight: 1.3 }}>
          {subtitle ?? defaults[type].subtitle}
        </div>
      )}
    </>
  );

  const actionDisabled = disabled || workflowDisabled;

  return asButton ? (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={actionDisabled}
        className={className ?? 'btn-link text-decoration-none'}
        style={style}
      >
        {content}
      </button>
      {type === 'bank' && bankConnectUrl && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1600, background: 'rgba(0, 0, 0, 0.5)' }}>
          <div
            style={{
              width: 'min(1100px, 96vw)',
              height: 'min(760px, 94vh)',
              margin: '3vh auto',
              background: '#fff',
              borderRadius: 8,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  cancel();
                  closeBankModal(false);
                }}
              >
                Close
              </button>
            </div>
            {bankConnectError ? (
              <div style={{ padding: '12px 16px', color: '#b42318' }}>
                {bankConnectError}
              </div>
            ) : (
              <div
                ref={bankEmbedNodeRef}
                style={{ width: '100%', flex: 1, position: 'relative' }}
              />
            )}
          </div>
        </div>
      )}
    </>
  ) : (
    <>
      <a
        href="#"
        onClick={handleClick}
        className={className}
        style={style}
        aria-disabled={actionDisabled}
      >
        {content}
      </a>
      {type === 'bank' && bankConnectUrl && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1600, background: 'rgba(0, 0, 0, 0.5)' }}>
          <div
            style={{
              width: 'min(1100px, 96vw)',
              height: 'min(760px, 94vh)',
              margin: '3vh auto',
              background: '#fff',
              borderRadius: 8,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  cancel();
                  closeBankModal(false);
                }}
              >
                Close
              </button>
            </div>
            {bankConnectError ? (
              <div style={{ padding: '12px 16px', color: '#b42318' }}>
                {bankConnectError}
              </div>
            ) : (
              <div
                ref={bankEmbedNodeRef}
                style={{ width: '100%', flex: 1, position: 'relative' }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ArgyleIncomeLink;
